import type { SireAncestry } from '../../models/SireAncestry';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import RHFInput from '../RHF/RHFInput';
import { RHFSubmitButton } from '../RHF/RHFSubmitButton';
import AncestryApi from '../../apis/ancestry_api';
import { handleError } from '../../apis';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { sireAncestorKeys } from '../../queries/AlpacaQueries';

const validator_sireAncestry = z.object({
  sireAncestryId: z.number().optional(),
  applicationUserId: z.string(),
  alpacaId: z.number(),
  sire: z.string().nullish(),
  sireParent1: z.string().nullish(),
  damParent1: z.string().nullish(),
  sireParent2: z.string().nullish(),
  damParent2: z.string().nullish(),
  sireParent3: z.string().nullish(),
  damParent3: z.string().nullish(),
  sireParent4: z.string().nullish(),
  damParent4: z.string().nullish(),
  sireParent5: z.string().nullish(),
  damParent5: z.string().nullish(),
});

type SireAncestryForm = z.infer<typeof validator_sireAncestry>;

export default function UpdateSireAncestryDisplay({
  sireAncestry,
  close,
}: {
  sireAncestry: SireAncestry;
  close: () => void;
}) {
  const queryClient = useQueryClient();

  const update = async (values: SireAncestryForm) => {
    try {
      if (values.sireAncestryId && values.sireAncestryId > 0) {
        await AncestryApi.updateSireAncestry(values?.alpacaId ?? 0, values);
      } else {
        await AncestryApi.createSireAncestry(values.alpacaId, values);
      }
      queryClient.invalidateQueries({
        queryKey: sireAncestorKeys.for(sireAncestry.alpacaId),
      });
      close();
    } catch (error) {
      console.log(error);
      const message = handleError(error);
      toast.error(`Error updating: ${message}`);
    }
  };
  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm<SireAncestryForm>({
    defaultValues: sireAncestry,
    resolver: zodResolver(validator_sireAncestry),
  });

  console.log(errors);
  return (
    <div className='sire-ancestry'>
      <form onSubmit={handleSubmit(update)}>
        <div className='update-ancestry-display'>
          <div className='sire-dam ms-2'>
            <RHFInput
              name='sire'
              label='Sire'
              type='text'
              register={register('sire')}
              errors={errors}
            />
          </div>
          <div className='second-generation-sire flex flex-col'>
            <RHFInput
              label='2nd Generation Sire'
              type='text'
              register={register('sireParent1')}
              errors={errors}
              name='sireParent1'
            />
          </div>
          <div className='second-generation-dam flex flex-col'>
            <RHFInput
              label='2nd Generation Dam'
              type='text'
              name='damParent1'
              register={register('damParent1')}
              errors={errors}
            />
          </div>
          <div className='third-generation-sire-sire flex flex-col'>
            <RHFInput
              label='3rd Generation Sire'
              type='text'
              name='sireParent2'
              register={register('sireParent2')}
              errors={errors}
            />
          </div>
          <div className='third-generation-dam-sire flex flex-col'>
            <RHFInput
              label='3rd Generation Dam'
              type='text'
              name='damParent2'
              register={register('damParent2')}
              errors={errors}
            />
          </div>
          <div className='third-generation-sire-dam flex flex-col'>
            <RHFInput
              label='3rd Generation Sire'
              type='text'
              name='sireParent3'
              register={register('sireParent3')}
              errors={errors}
            />
          </div>
          <div className='third-generation-dam-dam flex flex-col'>
            <RHFInput
              label='3rd Generation Dam'
              type='text'
              name='damParent3'
              register={register('damParent3')}
              errors={errors}
            />
          </div>
          <div className='sire-submit'>
            <RHFSubmitButton label='Submit' isSubmitting={isSubmitting} />
          </div>
        </div>
      </form>
    </div>
  );
}
